import Vue from 'vue';
import {emptyListResponse} from '@/domain/core/utils/django';

export default (service) => ({
    namespaced: true,

    state: {
        scope: null, // {type: 'global' | 'contract-manager' | 'pica-service', id: number}
        collaborators: {...emptyListResponse},
        availableRoles: [],
        pendingChanges: {}, // {collaboratorId: {roleId: boolean}}
        loading: {
            collaborators: false,
            roles: false,
            update: false
        },
        filters: {
            status: null,
            search: '',
            roleName: null,
            page: 1,
            pageSize: 20
        },
        selectedCollaborator: null,
        collaboratorPermissions: null,
        // Virtual Users
        virtualUserCredentials: null // Stores credentials after create/reset (shown only once)
    },
    
    getters: {
        hasScope: (state) => state.scope !== null,
        
        collaborators: (state) => state.collaborators,
        
        availableRoles: (state) => state.availableRoles || [],
        
        pendingChanges: (state) => state.pendingChanges,
        
        inheritedCollaborators: (state) => (state.collaborators.results || [])
            .filter(c => c.is_inherited === true),
        
        directCollaborators: (state) => (state.collaborators.results || [])
            .filter(c => c.is_inherited === false),
        
        pendingInvitations: (state) => (state.collaborators.results || [])
            .filter(c => c.status === 'pending'),
        
        activeCollaborators: (state) => (state.collaborators.results || [])
            .filter(c => c.status === 'active'),
        
        disabledCollaborators: (state) => (state.collaborators.results || [])
            .filter(c => c.status === 'disabled'),
        
        hasPendingChanges: (state) => Object.keys(state.pendingChanges).length > 0,
        
        getPendingChangesForCollaborator: (state) => (collaboratorId) => {
            return state.pendingChanges[collaboratorId] || {};
        },

        collaboratorPermissions: (state) => state.collaboratorPermissions,

        // Virtual Users Getters
        virtualUsers: (state) => (state.collaborators.results || [])
            .filter(c => c.is_virtual === true),

        hasVirtualUsers: (state, getters) => getters.virtualUsers.length > 0,

        virtualUserCount: (state, getters) => getters.virtualUsers.length,

        virtualUserCredentials: (state) => state.virtualUserCredentials
    },
    
    mutations: {
        setScope(state, scope) {
            Vue.set(state, 'scope', scope);
        },
        
        setCollaborators(state, data) {
            Vue.set(state, 'collaborators', data);
        },
        
        setAvailableRoles(state, roles) {
            Vue.set(state, 'availableRoles', roles);
        },
        
        setLoading(state, { key, value }) {
            Vue.set(state.loading, key, value);
        },
        
        setFilters(state, filters) {
            Object.keys(filters).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    Vue.set(state.filters, key, filters[key]);
                }
            });
        },
        
        resetFilters(state) {
            Vue.set(state, 'filters', {
                status: null,
                search: '',
                roleName: null,
                page: 1,
                pageSize: 20
            });
        },
        
        addPendingChange(state, { collaboratorId, roleId, value }) {
            // Assicurati che gli ID siano numeri
            collaboratorId = parseInt(collaboratorId);
            roleId = parseInt(roleId);
            
            if (!state.pendingChanges[collaboratorId]) {
                Vue.set(state.pendingChanges, collaboratorId, {});
            }
            
            // Se il valore corrisponde allo stato originale, rimuovi la modifica pendente
            const collaborator = state.collaborators.results?.find(c => c.id === collaboratorId);
            if (collaborator) {
                const hasRole = collaborator.roles?.some(r => r.id === roleId);
                if (hasRole === value) {
                    // Il valore corrisponde allo stato originale, rimuovi la modifica
                    Vue.delete(state.pendingChanges[collaboratorId], roleId);
                    if (Object.keys(state.pendingChanges[collaboratorId]).length === 0) {
                        Vue.delete(state.pendingChanges, collaboratorId);
                    }
                    return;
                }
            }
            
            Vue.set(state.pendingChanges[collaboratorId], roleId, value);
        },
        
        removePendingChange(state, { collaboratorId, roleId }) {
            if (state.pendingChanges[collaboratorId]) {
                Vue.delete(state.pendingChanges[collaboratorId], roleId);
                if (Object.keys(state.pendingChanges[collaboratorId]).length === 0) {
                    Vue.delete(state.pendingChanges, collaboratorId);
                }
            }
        },
        
        clearPendingChanges(state) {
            Vue.set(state, 'pendingChanges', {});
        },
        
        setSelectedCollaborator(state, collaborator) {
            Vue.set(state, 'selectedCollaborator', collaborator);
        },
        
        setCollaboratorPermissions(state, permissions) {
            Vue.set(state, 'collaboratorPermissions', permissions);
        },
        
        updateCollaboratorInList(state, updatedCollaborator) {
            const index = state.collaborators.results.findIndex(
                c => c.id === updatedCollaborator.id
            );
            if (index !== -1) {
                Vue.set(state.collaborators.results, index, updatedCollaborator);
            }
        },
        
        removeCollaboratorFromList(state, collaboratorId) {
            const index = state.collaborators.results.findIndex(
                c => c.id === collaboratorId
            );
            if (index !== -1) {
                state.collaborators.results.splice(index, 1);
            }
        },

        // Virtual Users Mutations
        setVirtualUserCredentials(state, credentials) {
            Vue.set(state, 'virtualUserCredentials', credentials);
        },

        clearVirtualUserCredentials(state) {
            Vue.set(state, 'virtualUserCredentials', null);
        }
    },
    
    actions: {
        async setScope({ commit, dispatch }, scope) {
            commit('setScope', scope);
            commit('clearPendingChanges');
            // Fetch data for new scope
            try {
                await Promise.all([
                    dispatch('fetchCollaborators'),
                    dispatch('fetchAvailableRoles')
                ]);
            } catch (error) {
                // Don't re-throw to allow the component to render
            }
        },
        
        async fetchCollaborators({ commit, state }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            commit('setLoading', { key: 'collaborators', value: true });
            
            try {
                const data = await service.fetchCollaborators(state.scope, {
                    status: state.filters.status,
                    search: state.filters.search,
                    roleName: state.filters.roleName,
                    page: state.filters.page,
                    pageSize: state.filters.pageSize
                });
                
                // Mappa page_number a page per compatibilità con AjaxTable
                if (data && data.pagination && data.pagination.page_number) {
                    data.pagination.page = data.pagination.page_number;
                }
                
                // Il service restituisce direttamente i dati, non response.data
                commit('setCollaborators', data || {...emptyListResponse});
            } catch (error) {
                commit('setCollaborators', {...emptyListResponse}); // Set empty list on error
                throw error;
            } finally {
                commit('setLoading', { key: 'collaborators', value: false });
            }
        },
        
        async fetchAvailableRoles({ commit, state }, params = {}) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            commit('setLoading', { key: 'roles', value: true });
            
            try {
                const data = await service.fetchAvailableRoles(state.scope, params);
                // Il service restituisce direttamente i dati
                // Se è un oggetto con results, usa results, altrimenti usa direttamente data
                const roles = data?.results || data || [];
                commit('setAvailableRoles', Array.isArray(roles) ? roles : []);
            } catch (error) {
                commit('setAvailableRoles', []); // Set empty array on error
                throw error;
            } finally {
                commit('setLoading', { key: 'roles', value: false });
            }
        },
        
        async inviteCollaborators({ state, dispatch }, data) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const result = await service.inviteCollaborators(state.scope, data);
                await dispatch('fetchCollaborators');
                return result; // Ritorna success/errors per gestione nel componente
            } catch (error) {
                throw error;
            }
        },

        async assignCollaborators({ state, dispatch }, data) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const result = await service.assignCollaborators(state.scope, data);
                await dispatch('fetchCollaborators');
                return result; // Ritorna success/errors per gestione nel componente
            } catch (error) {
                throw error;
            }
        },
        
        async searchUsers({ state }, { searchTerm, excludeExisting = true }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                const data = await service.searchUsers(state.scope, searchTerm);
                // Filtra utenti già collaboratori se richiesto
                if (excludeExisting && data?.results) {
                    return data.results.filter(user => !user.already_collaborator);
                }
                return data?.results || [];
            } catch (error) {
                return [];
            }
        },
        
        async updateCollaboratorRoles({ state, commit, dispatch }, { collaboratorId, roles }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            commit('setLoading', { key: 'update', value: true });
            
            try {
                await service.updateCollaboratorRoles(state.scope, collaboratorId, roles);
                await dispatch('fetchCollaborators');
            } catch (error) {
                throw error;
            } finally {
                commit('setLoading', { key: 'update', value: false });
            }
        },
        
        async applyPendingChanges({ state, dispatch, commit }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            commit('setLoading', { key: 'update', value: true });
            
            try {
                const promises = [];
                
                // Process each collaborator with pending changes
                for (const [collaboratorId, roleChanges] of Object.entries(state.pendingChanges)) {
                    const collaborator = state.collaborators.results.find(c => c.id === parseInt(collaboratorId));
                    if (!collaborator) continue;
                    
                    // Build new roles array based on changes
                    const currentRoles = collaborator.roles || [];
                    const newRoles = [];
                    
                    // Add existing roles that aren't being removed
                    currentRoles.forEach(role => {
                        if (roleChanges[role.id] !== false) {
                            newRoles.push(role);
                        }
                    });
                    
                    // Add new roles
                    Object.entries(roleChanges).forEach(([roleId, shouldAdd]) => {
                        if (shouldAdd === true && !currentRoles.find(r => r.id === parseInt(roleId))) {
                            newRoles.push({ id: parseInt(roleId) });
                        }
                    });
                    
                    promises.push(
                        service.updateCollaboratorRoles(state.scope, collaboratorId, newRoles)
                    );
                }
                
                await Promise.all(promises);
                commit('clearPendingChanges');
                await dispatch('fetchCollaborators');
            } catch (error) {
                throw error;
            } finally {
                commit('setLoading', { key: 'update', value: false });
            }
        },
        
        cancelPendingChanges({ commit }) {
            commit('clearPendingChanges');
        },
        
        async disableCollaborator({ state, dispatch }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                await service.disableCollaborator(state.scope, collaboratorId);
                await dispatch('fetchCollaborators');
            } catch (error) {
                throw error;
            }
        },
        
        async enableCollaborator({ state, dispatch }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                await service.enableCollaborator(state.scope, collaboratorId);
                await dispatch('fetchCollaborators');
            } catch (error) {
                throw error;
            }
        },
        
        async resendInvitation({ state }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                await service.resendInvitation(state.scope, collaboratorId);
                // No need to refresh - just sending an email
            } catch (error) {
                throw error;
            }
        },
        
        async updateCollaboratorEmail({ state, dispatch }, { collaboratorId, email }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                await service.updateCollaboratorEmail(state.scope, collaboratorId, email);
                await dispatch('fetchCollaborators');
            } catch (error) {
                throw error;
            }
        },
        
        async forceAcceptInvitation({ state, dispatch }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                await service.forceAcceptInvitation(state.scope, collaboratorId);
                await dispatch('fetchCollaborators');
            } catch (error) {
                throw error;
            }
        },
        
        async fetchCollaboratorDetail({ state }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                return await service.getCollaboratorDetail(state.scope, collaboratorId);
            } catch (error) {
                throw error;
            }
        },

        async fetchCollaboratorPermissions({ state, commit }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const data = await service.getCollaboratorPermissions(state.scope, collaboratorId);
                commit('setCollaboratorPermissions', data);
                return data;
            } catch (error) {
                throw error;
            }
        },
        
        async fetchRolePermissions({ state }, roleId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                return await service.getRolePermissions(state.scope, roleId);
            } catch (error) {
                throw error;
            }
        },
        
        updateFilters({ commit, dispatch }, filters) {
            commit('setFilters', filters);
            if (filters.page === undefined) {
                commit('setFilters', { page: 1 });
            }
            return dispatch('fetchCollaborators');
        },
        
        changePage({ commit, dispatch }, page) {
            commit('setFilters', { page });
            return dispatch('fetchCollaborators');
        },
        
        toggleRoleChange({ commit }, { collaboratorId, roleId, value }) {
            if (value) {
                commit('addPendingChange', { collaboratorId, roleId, value: true });
            } else {
                commit('addPendingChange', { collaboratorId, roleId, value: false });
            }
        },

        async exportFtpCredentials({ state }, { type, gallery_id }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                await service.exportFtpCredentials(state.scope, { type, gallery_id });
            } catch (error) {
                throw error;
            }
        },

        // Virtual Users Actions
        async createVirtualUsers({ state, commit, dispatch }, data) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const response = await service.createVirtualUsers(state.scope, data);
                // Response: {collaborators: [...], credentials: [...], errors: {...}, success_count: N, error_count: N}

                // Store credentials for modal display (array of credentials)
                if (response.credentials && response.credentials.length > 0) {
                    commit('setVirtualUserCredentials', response.credentials);
                }

                // Refresh collaborators list
                await dispatch('fetchCollaborators');

                return response;
            } catch (error) {
                throw error;
            }
        },

        async resetAllVirtualUserPasswords({ state, commit }, collaboratorIds = []) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const response = await service.resetAllVirtualUserPasswords(state.scope, collaboratorIds);

                // Store array of credentials for bulk display
                // Response format: {message, count, credentials: [...], errors: {...}}
                if (response.credentials) {
                    commit('setVirtualUserCredentials', response.credentials);
                }

                return response;
            } catch (error) {
                throw error;
            }
        },

        clearVirtualUserCredentials({ commit }) {
            commit('clearVirtualUserCredentials');
        }
    }
});